import { FormikProvider } from 'formik';
import getConfig from 'next/config';
import { useEffect, useState } from 'react';
import { Box, Button, Grid, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { Banner, Link, Typography } from '@care/react-component-lib';

import blueWaveImage from '@/../public/blue-wave.svg';
import { useAppDispatch, useSeekerState } from '@/components/AppState';
import OverlaySpinner from '@/components/OverlaySpinner';
import ZipInput from '@/components/ZipInput';
import FormikInlineTextField from '@/components/blocks/FormikInlineTextField';
import EmailField from '@/components/features/accountCreation/EmailField';
import PasswordField from '@/components/features/accountCreation/PasswordField';
import {
  validateFirstName,
  validateLastName,
} from '@/components/features/accountCreation/accountCreationForm';
import useIsSinglePageEnrollment from '@/components/hooks/useIsSinglePageEnrollment';
import useSignUpForm from '@/components/hooks/useSignUpForm';
import { useSpecialCharsValidationOnNames } from '@/components/hooks/useSpecialCharsValidationOnNames';
import {
  AUTH_ROUTES,
  CLIENT_FEATURE_FLAGS,
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
} from '@/constants';
import { Location } from '@/types/common';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';

const {
  publicRuntimeConfig: { CZEN_GENERAL, CZEN_GENERAL_LOGIN },
} = getConfig();

const useStyles = makeStyles((theme) => ({
  formContainer: {
    minHeight: `max(930px, calc(100vh - 57px))`,
    backgroundImage: `url(${blueWaveImage.src})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100vw auto',
    backgroundPosition: 'bottom right',
    padding: theme.spacing(4, 0),
    [theme.breakpoints.up('sm')]: {
      minHeight: `calc(100vh - 57px)`,
      padding: theme.spacing(6, 0),
      backgroundSize: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      minHeight: 'calc(100vh - 105px)',
    },
  },
  formPanel: {
    minHeight: '740px',
    margin: theme.spacing(0, 3),
    [theme.breakpoints.up('sm')]: {
      backgroundColor: theme.palette.care.white,
      width: '504px',
      padding: theme.spacing(6),
      margin: theme.spacing(0, 'auto'),
      borderRadius: '24px',
      boxShadow: ' 0px 0px 16px rgba(0, 0, 0, 0.15) !important',
    },
  },
  disclaimer: {
    color: theme.palette.care.grey[600],
    [theme.breakpoints.up('sm')]: {
      textAlign: 'center',
    },
  },
  link: {
    textDecoration: 'none',
  },
  textField: {
    padding: '0',
  },
  zipInput: {
    '& > div': {
      padding: '0',
    },
  },
}));

function SignUpPage() {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { errorMessage, formik } = useSignUpForm();
  const { validateSpecialChars, softValidateSpecialChars, fireDedupedNameValidationEvent } =
    useSpecialCharsValidationOnNames();
  const { zipcode, city, state } = useSeekerState();
  const { seekerSinglePageEnrollmentFlag, isSeekerSinglePageEnrollment } =
    useIsSinglePageEnrollment();
  const [shouldGoNext, setShouldGoNext] = useState(false);
  const [validateZipOnClick, setValidateZipOnClick] = useState(false);
  const dispatch = useAppDispatch();

  const handleZipError = (e: boolean) => {
    formik.setStatus({ zipError: e });
  };

  const onZipInputChange = (location: Location) => {
    dispatch({ type: 'setZipcode', zipcode: location.zipcode });
    dispatch({ type: 'setCityAndState', city: location.city, state: location.state });
  };

  useEffect(() => {
    if (!shouldGoNext) {
      return;
    }

    setValidateZipOnClick(true);

    formik.submitForm();

    setShouldGoNext(false);
  }, [shouldGoNext]);

  useEffect(() => {
    dispatch({
      type: 'updateFeatureFlagSharedData',
      flagName: CLIENT_FEATURE_FLAGS.GROWTH_SEO_SEEKER_SINGLE_PAGE_ENROLLMENT,
      flagValue: seekerSinglePageEnrollmentFlag?.value,
    });
  }, []);

  if (formik.isSubmitting) {
    return <OverlaySpinner isOpen wrapped />;
  }

  return (
    <FormikProvider value={formik}>
      <Box className={classes.formContainer}>
        <Box className={classes.formPanel}>
          <Grid container>
            {errorMessage && (
              <Grid item xs={12}>
                <Box mb={2}>
                  <Banner type="warning" roundCorners>
                    {errorMessage}
                  </Banner>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="h1">Create a free account</Typography>
              <Box mt={1}>
                <Typography variant="body2">
                  See caregivers who match your needs. It only takes a few seconds.
                </Typography>
              </Box>
            </Grid>

            <Grid container>
              <Grid item xs={12} sm={6}>
                <Box mr={isDesktop ? 1 : 0}>
                  <FormikInlineTextField
                    id="firstName"
                    name="firstName"
                    label="First name"
                    validate={(firstNameInput: string) =>
                      validateFirstName(
                        firstNameInput,
                        validateSpecialChars,
                        softValidateSpecialChars
                      )
                    }
                    fireValidationErrorEvent={() =>
                      fireDedupedNameValidationEvent(isSeekerSinglePageEnrollment)
                    }
                    classes={{ root: classes.textField }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box ml={isDesktop ? 1 : 0}>
                  <FormikInlineTextField
                    id="lastName"
                    name="lastName"
                    label="Last name"
                    validate={(lastNameInput: string) =>
                      validateLastName(
                        lastNameInput,
                        validateSpecialChars,
                        softValidateSpecialChars
                      )
                    }
                    fireValidationErrorEvent={() =>
                      fireDedupedNameValidationEvent(isSeekerSinglePageEnrollment)
                    }
                    classes={{ root: classes.textField }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <ZipInput
                  location={{ zipcode, city, state }}
                  onError={handleZipError}
                  onChange={onZipInputChange}
                  validateOnLostFocusOrClick
                  validateOnClick={validateZipOnClick}
                  setValidateOnClick={setValidateZipOnClick}
                  setShouldGoNext={setShouldGoNext}
                  showLocationIcon={false}
                  className={classes.zipInput}
                />
              </Grid>

              <Grid item xs={12}>
                <EmailField
                  id="email"
                  name="email"
                  label="Email address"
                  czenGeneralLoginUrl={CZEN_GENERAL_LOGIN}
                  showErrorWhenTouched
                  classes={{ root: classes.textField }}
                />
              </Grid>

              <Grid item xs={12}>
                <PasswordField
                  id="password"
                  name="password"
                  label="Password"
                  showErrorWhenTouched
                  classes={{ root: classes.textField }}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Box mt={4}>
                <Typography variant="info1" className={classes.disclaimer}>
                  <span>By clicking &quot;Join now&quot; you agree to our</span>{' '}
                  <Link variant="info1" href={TERMS_OF_USE_URL} className={classes.link}>
                    Terms of Use
                  </Link>
                  <span>&nbsp;and</span>{' '}
                  <Link variant="info1" href={PRIVACY_POLICY_URL} className={classes.link}>
                    Privacy Policy
                  </Link>{' '}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box mt={2}>
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => setShouldGoNext(true)}>
                  Join now
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box mt={3} textAlign="center">
                <Typography variant="body3" display="inline">
                  Already a Care member?{' '}
                </Typography>
                <Link
                  onClick={() =>
                    AnalyticsHelper.logEvent({
                      name: 'CTA Interacted',
                      data: {
                        cta_text: 'Log in',
                        cta_location: 'Single Page Enrollment',
                        screen_name: 'Single Page Enrollment',
                      },
                    })
                  }
                  href={`${CZEN_GENERAL}${AUTH_ROUTES.LOGIN}`}
                  variant="link3"
                  bold
                  inline
                  className={classes.link}>
                  Log in
                </Link>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </FormikProvider>
  );
}

export default SignUpPage;
